<template>
  <div class="lt-bar">
    <!-- Left: selection gestures (list) or back (detail), then the actions. -->
    <div class="lt-barLeft">
      <template v-if="mode === 'list'">
        <div ref="selectMenuEl" class="lt-masterSelect">
          <input
            type="checkbox"
            :checked="pageFullySelected"
            :indeterminate.prop="someSelected && !pageFullySelected"
            :aria-label="pageFullySelected ? 'Deselect all' : 'Select page'"
            title="Select page"
            @change="$emit('toggle-page')"
          />
          <button
            type="button"
            class="lt-masterCaret"
            aria-label="Selection options"
            :aria-expanded="selectMenuOpen"
            aria-haspopup="menu"
            @click="selectMenuOpen = !selectMenuOpen"
          >
            <AppIcon name="chevron_down" :size="12" />
          </button>
          <div v-if="selectMenuOpen" class="lt-selectMenu" role="menu">
            <button type="button" role="menuitem" class="lt-selectMenuItem" @click="pick('page')">
              All on this page
            </button>
            <button type="button" role="menuitem" class="lt-selectMenuItem" @click="pick('none')">
              None
            </button>
          </div>
        </div>
      </template>

      <button
        v-else
        type="button"
        class="iconBtn"
        title="Back to list"
        aria-label="Back to list"
        @click="$emit('back')"
      >
        <AppIcon name="arrow_right" :size="16" class="flip" />
      </button>

      <span v-if="mode === 'list' && someSelected" class="lt-count">{{ selectedCount }} selected</span>

      <!-- The three consequential actions keep their labels. Approve & Send is an
           irreversible outbound send — an icon with a tooltip understates it. -->
      <div v-if="actionsEnabled" class="actions">
        <button
          type="button"
          class="btn secondary"
          :disabled="!revisableCount"
          :title="eligibilityTitle(revisableCount, 'revised')"
          @click="$emit('revise')"
        >
          <AppIcon name="reachout" :size="16" />
          <span>Revise</span>
        </button>
        <button
          type="button"
          class="btn"
          :disabled="approving || !approvableCount"
          :title="eligibilityTitle(approvableCount, 'sent')"
          @click="$emit('approve')"
        >
          <AppIcon name="reachout" :size="16" />
          <span>{{ approving ? 'Sending…' : 'Approve & Send' }}</span>
        </button>
        <button
          type="button"
          class="btn danger"
          :disabled="!deletableCount"
          :title="eligibilityTitle(deletableCount, 'deleted')"
          @click="$emit('delete')"
        >
          Delete…
        </button>
      </div>
    </div>

    <!-- Right: paging (list) or reachout-to-reachout stepping (detail). -->
    <div class="lt-barRight">
      <div v-if="mode === 'list'" ref="sortMenuEl" class="lt-sortMenuWrap">
        <button
          type="button"
          class="lt-rangeBtn"
          :aria-expanded="sortMenuOpen"
          aria-haspopup="menu"
          title="Change sort order"
          @click="sortMenuOpen = !sortMenuOpen"
        >
          {{ rangeLabel }}
        </button>
        <div v-if="sortMenuOpen" class="lt-sortMenu" role="menu">
          <button
            v-for="opt in SORT_OPTIONS"
            :key="opt.value"
            type="button"
            role="menuitem"
            class="lt-sortMenuItem"
            :class="{ active: sort === opt.value }"
            @click="pickSort(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
      <span v-else class="lt-rangeStatic">{{ rangeLabel }}</span>

      <button
        type="button"
        class="lt-pagerBtn"
        :disabled="!canPrev"
        :title="mode === 'list' ? 'Previous page' : 'Previous (k)'"
        @click="$emit('prev')"
      >
        ‹
      </button>
      <button
        type="button"
        class="lt-pagerBtn"
        :disabled="!canNext"
        :title="mode === 'list' ? 'Next page' : 'Next (j)'"
        @click="$emit('next')"
      >
        ›
      </button>
    </div>
  </div>

  <!-- Escalation banner: only once the whole page is selected, so the offer to
       widen the selection appears exactly when it's the natural next step. -->
  <div v-if="mode === 'list' && pageFullySelected && escalationAvailable" class="lt-escalation">
    <template v-if="allMatchingSelected">
      All <strong>{{ selectedCount }}</strong> actions matching this filter are selected.
      <button type="button" class="lt-escalationLink" @click="$emit('toggle-page')">Clear selection</button>
    </template>
    <template v-else>
      All <strong>{{ pageCountLabel }}</strong> actions on this page are selected.
      <button
        type="button"
        class="lt-escalationLink"
        :disabled="selectingAll"
        @click="$emit('select-all-matching')"
      >
        {{ selectingAll ? 'Selecting…' : `Select all ${escalationTarget} matching this filter` }}
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'

/**
 * The unified header bar, shared by the Actions list and the reachout detail.
 *
 * One bar, two modes. On the list the right side pages through results; on the
 * detail the same control steps reachout-to-reachout, so the affordance stays
 * in the same place as you drill in and out.
 *
 * Selection lives in the master checkbox (page-level) plus the escalation
 * banner (whole filter), which is what lets the three real actions keep their
 * labels — an icon-only "Approve & Send" would understate an irreversible send.
 */

const props = defineProps<{
  mode: 'list' | 'detail'
  /** Rows on the current page, for the master checkbox state. */
  pageSize?: number
  selectedCount?: number
  pageFullySelected?: boolean
  /** Total matching the filter, for the escalation offer. */
  total?: number
  /** Cap on how many "select all" can reach. */
  selectAllMax?: number
  selectingAll?: boolean
  approvableCount?: number
  deletableCount?: number
  revisableCount?: number
  approving?: boolean
  rangeLabel: string
  sort?: string
  canPrev?: boolean
  canNext?: boolean
}>()

const emit = defineEmits<{
  'toggle-page': []
  'select-all-matching': []
  'select-none': []
  revise: []
  approve: []
  delete: []
  prev: []
  next: []
  back: []
  'update:sort': [string]
}>()

const SORT_OPTIONS = [
  { value: 'newest_first', label: 'Newest' },
  { value: 'oldest_first', label: 'Oldest' },
]

const sortMenuOpen = ref(false)
const selectMenuOpen = ref(false)
const sortMenuEl = ref<HTMLElement | null>(null)
const selectMenuEl = ref<HTMLElement | null>(null)

const someSelected = computed(() => (props.selectedCount ?? 0) > 0)

function eligibilityTitle(eligible: number | undefined, verb: string): string | undefined {
  const n = eligible ?? 0
  const selected = props.selectedCount ?? 0
  if (!n) return `None of the selected reachouts can be ${verb}`
  if (n < selected) return `${n} of ${selected} selected can be ${verb} — the rest are skipped`
  return undefined
}
/**
 * Only the list bar carries action buttons. On the detail page ReachoutModal's
 * footer owns Delete / Revise / Edit / Approve for the open reachout, with
 * per-reachout enablement (approval blocks, edit state) this bar can't see —
 * rendering them here produced a row of permanently-disabled duplicates.
 */
const actionsEnabled = computed(() => props.mode === 'list' && someSelected.value)

const escalationTarget = computed(() => Math.min(props.total ?? 0, props.selectAllMax ?? Infinity))
const escalationAvailable = computed(() => (props.total ?? 0) > (props.pageSize ?? 0))
const allMatchingSelected = computed(() => (props.selectedCount ?? 0) >= escalationTarget.value)
const pageCountLabel = computed(() => String(props.pageSize ?? 0))

function pickSort(value: string) {
  sortMenuOpen.value = false
  if (value !== props.sort) emit('update:sort', value)
}

function pick(which: 'page' | 'none') {
  selectMenuOpen.value = false
  if (which === 'none') emit('select-none')
  else if (!props.pageFullySelected) emit('toggle-page')
}

function onPointerDown(e: PointerEvent) {
  const t = e.target
  if (!(t instanceof Node)) return
  if (sortMenuOpen.value && sortMenuEl.value && !sortMenuEl.value.contains(t)) sortMenuOpen.value = false
  if (selectMenuOpen.value && selectMenuEl.value && !selectMenuEl.value.contains(t)) selectMenuOpen.value = false
}

onMounted(() => document.addEventListener('pointerdown', onPointerDown))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onPointerDown))

/** Lets the parent fire the confetti after its own async work. */
</script>

<style scoped>
/* Everything the list-mode template uses (.lt-bar, .lt-masterSelect,
   .lt-rangeBtn, .lt-pagerBtn, .lt-escalation, etc.) lives in the shared "List
   toolbar primitive" section of ../../styles.css, so a restyle there reaches
   every paged list at once. What stays local here is detail-mode-only: the
   back button and the static (non-clickable) range label. */
.actions { display: flex; align-items: center; gap: var(--space-2); }

/* ── Back (detail mode) ────────────────────────────────────────── */
.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
}
.iconBtn:hover { color: var(--color-text-primary); background: var(--color-bg-subtle); }
.flip { transform: rotate(180deg); }

/* Detail mode has no sort menu to open, so the range label is inert text
   rather than .lt-rangeBtn's button. */
.rangeStatic {
  font-size: var(--fs-xs);
  color: var(--color-text-tertiary);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
